import React, { useState, useEffect } from 'react';
import { Clock, Users, TrendingUp, Award, Download, Calendar, BarChart3, Target, Zap } from 'lucide-react';
import TrialConnxLogo from './assets/tco-logo-text.svg';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import awsExports from './aws-exports';
import { createAssessment, updateAssessment } from './graphql/mutations';
import LandingPage from './components/LandingPage';
import ProcessFlow from './components/ProcessFlow';

Amplify.configure(awsExports);
const client = generateClient();

const StudySpeedCalculator = () => {
  const [currentPage, setCurrentPage] = useState('landing'); // 'landing', 'process', 'results'
  const [currentStep, setCurrentStep] = useState(0);
  
  // Initialize with saved data or defaults
  const getInitialData = () => {
    const saved = localStorage.getItem('studySpeedData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Could not parse saved data:', e);
      }
    }
    return {
      siteInfo: {
        organizationName: "",
        siteType: "",
        region: "",
        annualVolume: 10
      },
      responseTimes: {
        averageResponse: 15,
        simpleStudies: 7,
        complexStudies: 21,
        rushRequests: 3
      },
      staffResources: {
        dedicatedFTE: 2,
        experienceLevel: "",
        currentTools: [],
        peakCapacity: ""
      },
      studyPortfolio: {
        therapeuticAreas: [],
        studyPhases: [],
        internationalPercentage: 25,
        enrollmentTargets: ""
      },
      processAssessment: {
        documentationProcess: "",
        approvalStages: 4,
        commonDelays: []
      },
      contactInfo: {
        name: "",
        email: "",
        phone: "",
        organization: "",
        bestTimeToContact: "",
        interests: []
      }
    };
  };
  
  const [assessmentData, setAssessmentData] = useState(getInitialData);

  const [calculatedResults, setCalculatedResults] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Industry benchmarks
  const industryBenchmarks = {
    responseTimesByType: {
      simple: { excellent: [1,5], good: [6,10], needsImprovement: [11,30] },
      complex: { excellent: [7,14], good: [15,21], needsImprovement: [22,45] },
      rush: { excellent: [1,2], good: [3,4], needsImprovement: [5,7] }
    },
    staffUtilization: {
      studiesPerFTE: { excellent: [12,20], good: [8,11], needsImprovement: [1,7] }
    },
    automationLevels: {
      "Fully manual": 20,
      "Partially automated": 60,
      "Mostly automated": 90
    }
  };

  const steps = [
    { title: "Site Information", icon: Users },
    { title: "Response Times", icon: Clock },
    { title: "Staff & Resources", icon: Users },
    { title: "Study Portfolio", icon: TrendingUp },
    { title: "Process Assessment", icon: BarChart3 }
  ];

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('studySpeedData', JSON.stringify(assessmentData));
  }, [assessmentData]);

  const updateAssessmentData = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const calculateScore = () => {
    let speedScore = 0;
    let efficiencyScore = 0;
    let qualityScore = 0;
    let growthScore = 0;

    // Speed Component (40% weight)
    const { simple, complex, rush } = industryBenchmarks.responseTimesByType;
    
    // Simple studies scoring
    if (assessmentData.responseTimes.simpleStudies <= simple.excellent[1]) speedScore += 35;
    else if (assessmentData.responseTimes.simpleStudies <= simple.good[1]) speedScore += 25;
    else speedScore += 10;

    // Complex studies scoring
    if (assessmentData.responseTimes.complexStudies <= complex.excellent[1]) speedScore += 35;
    else if (assessmentData.responseTimes.complexStudies <= complex.good[1]) speedScore += 25;
    else speedScore += 10;

    // Rush requests scoring
    if (assessmentData.responseTimes.rushRequests <= rush.excellent[1]) speedScore += 30;
    else if (assessmentData.responseTimes.rushRequests <= rush.good[1]) speedScore += 20;
    else speedScore += 5;

    // Efficiency Component (30% weight)
    const studiesPerFTE = assessmentData.siteInfo.annualVolume / assessmentData.staffResources.dedicatedFTE;
    if (studiesPerFTE >= 12) efficiencyScore += 40;
    else if (studiesPerFTE >= 8) efficiencyScore += 30;
    else efficiencyScore += 15;

    // Automation level
    const automationLevel = industryBenchmarks.automationLevels[assessmentData.processAssessment.documentationProcess] || 20;
    efficiencyScore += (automationLevel / 100) * 35;

    // Peak capacity handling
    const capacityScores = {
      "Excess capacity": 25,
      "Usually manageable": 20,
      "Sometimes stretched": 15,
      "Often overwhelmed": 5
    };
    efficiencyScore += capacityScores[assessmentData.staffResources.peakCapacity] || 10;

    // Quality Component (20% weight)
    const experienceScores = {
      "Senior 5+ years": 60,
      "Mid-level 2-5 years": 45,
      "Junior <2 years": 30
    };
    qualityScore += experienceScores[assessmentData.staffResources.experienceLevel] || 30;

    // Process optimization (approval stages)
    if (assessmentData.processAssessment.approvalStages <= 3) qualityScore += 25;
    else if (assessmentData.processAssessment.approvalStages <= 5) qualityScore += 20;
    else qualityScore += 10;

    // Tool sophistication
    const toolScore = assessmentData.staffResources.currentTools.length * 3;
    qualityScore += Math.min(toolScore, 15);

    // Growth Readiness (10% weight)
    growthScore += assessmentData.studyPortfolio.therapeuticAreas.length * 15;
    growthScore += assessmentData.studyPortfolio.studyPhases.length * 10;
    growthScore += (assessmentData.studyPortfolio.internationalPercentage / 100) * 30;
    growthScore = Math.min(growthScore, 100);

    // Final weighted score
    const finalScore = Math.round(
      (speedScore * 0.4) + 
      (efficiencyScore * 0.3) + 
      (qualityScore * 0.2) + 
      (growthScore * 0.1)
    );

    return {
      total: finalScore,
      speed: Math.round(speedScore),
      efficiency: Math.round(efficiencyScore),
      quality: Math.round(qualityScore),
      growth: Math.round(growthScore)
    };
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Industry Leading";
    if (score >= 60) return "Above Average";
    if (score >= 40) return "Room for Improvement";
    return "Needs Attention";
  };

  const generateRecommendations = (scores) => {
    const recommendations = [];
    
    if (scores.speed < 70) {
      recommendations.push({
        title: "Accelerate Response Times",
        description: "Implement automated feasibility screening and standardized response templates to reduce average response time by 40-60%.",
        impact: "High"
      });
    }
    
    if (scores.efficiency < 60) {
      recommendations.push({
        title: "Optimize Staff Utilization",
        description: "Deploy workflow automation tools to handle routine tasks, allowing staff to focus on complex feasibility assessments.",
        impact: "High"
      });
    }
    
    if (scores.quality < 65) {
      recommendations.push({
        title: "Streamline Approval Process",
        description: "Reduce approval stages through parallel processing and automated checks to accelerate decision-making.",
        impact: "Medium"
      });
    }
    
    if (assessmentData.processAssessment.documentationProcess === "Fully manual") {
      recommendations.push({
        title: "Implement Process Automation",
        description: "Transition to automated documentation systems to reduce manual errors and improve consistency.",
        impact: "High"
      });
    }
    
    if (scores.growth < 50) {
      recommendations.push({
        title: "Expand Study Portfolio Capacity",
        description: "Leverage automation to handle increased study volume and diversify therapeutic area expertise.",
        impact: "Medium"
      });
    }
    
    return recommendations.slice(0, 4);
  };

  const calculateROI = (scores) => {
    const currentResponseTime = assessmentData.responseTimes.averageResponse;
    const potentialImprovement = Math.max(3, currentResponseTime * 0.4); // 60% improvement
    const timeSavingsPerMonth = (currentResponseTime - potentialImprovement) * assessmentData.siteInfo.annualVolume * 2; // 2 hours per study
    
    return {
      timeSavingsHours: Math.round(timeSavingsPerMonth),
      costReduction: Math.round(timeSavingsPerMonth * 75), // $75/hour
      capacityIncrease: Math.round(timeSavingsPerMonth / 8), // Additional studies capacity
      revenueImpact: Math.round(timeSavingsPerMonth * 150) // $150/hour revenue
    };
  };

  // When results are generated, sync to Amplify
  const handleResultsGenerated = async (assessmentData, calculatedResults) => {
    setIsSyncing(true);
    setSyncError(null);
    try {
      const input = {
        siteInfo: assessmentData.siteInfo,
        responseTimes: assessmentData.responseTimes,
        staffResources: assessmentData.staffResources,
        studyPortfolio: assessmentData.studyPortfolio,
        processAssessment: assessmentData.processAssessment,
        calculatedResults: {
          total: calculatedResults.total,
          speed: calculatedResults.speed,
          efficiency: calculatedResults.efficiency,
          quality: calculatedResults.quality,
          growth: calculatedResults.growth,
          percentile: calculatedResults.percentile,
          roi: calculatedResults.roi
          // Note: recommendations excluded as schema shows type null
        }
      };
      const result = await client.graphql({ query: createAssessment, variables: { input } });
      setAssessmentId(result.data.createAssessment.id);
    } catch (err) {
      setSyncError('Failed to sync with cloud.');
      console.error('Amplify sync error:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  // When user submits CTA/contact form, update the assessment with contact info
  const handleContactSubmit = async (contactInfo) => {
    if (!assessmentId) return;
    setIsSyncing(true);
    setSyncError(null);
    try {
      const input = {
        id: assessmentId,
        contactInfo,
      };
      await client.graphql({ query: updateAssessment, variables: { input } });
      alert('Thank you! We will contact you soon with your detailed assessment report.');
    } catch (err) {
      setSyncError('Failed to update contact info in cloud.');
      console.error('Amplify update error:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleGetStarted = () => {
    setCurrentPage('process');
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const scores = calculateScore();
      const recommendations = generateRecommendations(scores);
      const roi = calculateROI(scores);
      
      const results = {
        total: scores.total,
        speed: scores.speed,
        efficiency: scores.efficiency,
        quality: scores.quality,
        growth: scores.growth,
        percentile: Math.min(95, Math.max(5, scores.total + Math.random() * 10)),
        roi,
        recommendations,
        // Keep original structure for UI
        scores
      };
      
      console.log('=== CALCULATION DEBUG ===');
      console.log('Calculated scores:', scores);
      console.log('Calculated ROI:', roi);
      console.log('Final results object:', results);
      console.log('=== END CALCULATION DEBUG ===');
      
      setCalculatedResults(results);
      setCurrentPage('results');
      // Sync to Amplify
      await handleResultsGenerated(assessmentData, results);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0: return true; // Site info is optional
      case 1: return true; // Response times have default values
      case 2: 
        return assessmentData.staffResources.experienceLevel && 
               assessmentData.staffResources.peakCapacity;
      case 3: 
        return assessmentData.studyPortfolio.therapeuticAreas.length > 0 &&
               assessmentData.studyPortfolio.studyPhases.length > 0 &&
               assessmentData.studyPortfolio.enrollmentTargets;
      case 4: 
        return assessmentData.processAssessment.documentationProcess;
      default: return true;
    }
  };

  // Show landing page
  if (currentPage === 'landing') {
    return <LandingPage onGetStarted={handleGetStarted} />;
  }

  // Show results page
  if (currentPage === 'results' && calculatedResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex flex-col items-center justify-center mb-6">
              <img src={TrialConnxLogo} alt="TrialConnx" className="h-6 mb-3" />
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 ml-4">Your StudySpeed Results</h1>
              </div>
            </div>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Based on your assessment, here's how your site performs against industry benchmarks
            </p>
          </div>

          {/* Main Score Display */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 hover-lift fade-in">
            <div className="text-center">
              <div className={`text-6xl font-bold mb-4 ${getScoreColor(calculatedResults.scores.total)}`}>
                {calculatedResults.scores.total}
              </div>
              <div className={`text-xl font-semibold mb-2 ${getScoreColor(calculatedResults.scores.total)}`}>
                {getScoreLabel(calculatedResults.scores.total)}
              </div>
              <div className="text-gray-600 mb-6">
                Your site performs better than {Math.round(calculatedResults.percentile)}% of similar research sites
              </div>
              
              {/* Score Breakdown */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                <div className="text-center group">
                  <div className="text-2xl font-bold text-primary-600 mb-1 group-hover:scale-110 transition-transform duration-200">{calculatedResults.scores.speed}</div>
                  <div className="text-sm text-gray-600">Speed</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-primary-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${calculatedResults.scores.speed}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-center group">
                  <div className="text-2xl font-bold text-success-600 mb-1 group-hover:scale-110 transition-transform duration-200">{calculatedResults.scores.efficiency}</div>
                  <div className="text-sm text-gray-600">Efficiency</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-success-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${calculatedResults.scores.efficiency}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-center group">
                  <div className="text-2xl font-bold text-info-600 mb-1 group-hover:scale-110 transition-transform duration-200">{calculatedResults.scores.quality}</div>
                  <div className="text-sm text-gray-600">Quality</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-info-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${calculatedResults.scores.quality}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-center group">
                  <div className="text-2xl font-bold text-warning-600 mb-1 group-hover:scale-110 transition-transform duration-200">{calculatedResults.scores.growth}</div>
                  <div className="text-sm text-gray-600">Growth</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-warning-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${calculatedResults.scores.growth}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROI Projections */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="w-6 h-6 mr-3 text-success-600" />
              Your Improvement Potential
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-primary-50 rounded-lg p-6">
                <div className="text-2xl font-bold text-primary-600 mb-2">
                  {calculatedResults.roi.timeSavingsHours} hrs
                </div>
                <div className="text-sm text-gray-600">Time Savings/Month</div>
              </div>
              <div className="bg-success-50 rounded-lg p-6">
                <div className="text-2xl font-bold text-success-600 mb-2">
                  ${calculatedResults.roi.costReduction.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Cost Reduction/Month</div>
              </div>
              <div className="bg-info-50 rounded-lg p-6">
                <div className="text-2xl font-bold text-info-600 mb-2">
                  +{calculatedResults.roi.capacityIncrease}
                </div>
                <div className="text-sm text-gray-600">Additional Studies</div>
              </div>
              <div className="bg-warning-50 rounded-lg p-6">
                <div className="text-2xl font-bold text-warning-600 mb-2">
                  ${calculatedResults.roi.revenueImpact.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Revenue Impact/Month</div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Target className="w-6 h-6 mr-3 text-primary-600" />
              Personalized Recommendations
            </h2>
            <div className="space-y-6">
              {calculatedResults.recommendations.map((rec, index) => (
                <div key={index} className="border-l-4 border-primary-500 pl-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{rec.title}</h3>
                      <p className="text-gray-600">{rec.description}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      rec.impact === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {rec.impact} Impact
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How TrialConnx Helps */}
          <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-8">
            <div className="text-center mb-8">
              <img src={TrialConnxLogo} alt="TrialConnx" className="h-8 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How TrialConnx Delivers These Results</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                TrialConnx specializes in transforming clinical research sites through smart feasibility solutions, 
                process optimization, and advanced automation tools.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Process Automation</h3>
                <p className="text-sm text-gray-600">Streamline your feasibility workflows with our proven automation solutions</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Performance Analytics</h3>
                <p className="text-sm text-gray-600">Data-driven insights to continuously improve your response times and efficiency</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Expert Consulting</h3>
                <p className="text-sm text-gray-600">Dedicated support from clinical research optimization specialists</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-3xl font-bold mb-4">Ready to Accelerate Your Site?</h2>
              <p className="text-xl opacity-90">
                Let's discuss how TrialConnx can help you achieve these improvements
              </p>
            </div>
            
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                handleContactSubmit(assessmentData.contactInfo);
              }}
            >
              {isSyncing && <div>Syncing with cloud...</div>}
              {syncError && <div className="text-red-500">{syncError}</div>}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <input
                  type="text"
                  name="entry.782630873"
                  placeholder="Your Name *"
                  className="w-full px-4 py-4 rounded-lg border-0 focus:ring-2 focus:ring-primary-300 touch-manipulation text-base"
                  value={assessmentData?.contactInfo?.name || ''}
                  onChange={(e) => updateAssessmentData('contactInfo', 'name', e.target.value)}
                  required
                />
                <input
                  type="email"
                  name="entry.512419933"
                  placeholder="Email Address *"
                  className="w-full px-4 py-4 rounded-lg border-0 focus:ring-2 focus:ring-primary-300 touch-manipulation text-base"
                  value={assessmentData?.contactInfo?.email || ''}
                  onChange={(e) => updateAssessmentData('contactInfo', 'email', e.target.value)}
                  required
                />
                <input
                  type="tel"
                  name="entry.264511547"
                  placeholder="Phone Number"
                  className="w-full px-4 py-4 rounded-lg border-0 focus:ring-2 focus:ring-primary-300 touch-manipulation text-base"
                  value={assessmentData?.contactInfo?.phone || ''}
                  onChange={(e) => updateAssessmentData('contactInfo', 'phone', e.target.value)}
                />
                <select
                  name="entry.329360515"
                  className="w-full px-4 py-4 rounded-lg border-0 focus:ring-2 focus:ring-primary-300 touch-manipulation text-base"
                  value={assessmentData?.contactInfo?.bestTimeToContact || ''}
                  onChange={(e) => updateAssessmentData('contactInfo', 'bestTimeToContact', e.target.value)}
                >
                  <option value="">Best Time to Contact</option>
                  <option value="Morning (9AM-12PM)">Morning (9AM-12PM)</option>
                  <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
                  <option value="Evening (5PM-8PM)">Evening (5PM-8PM)</option>
                </select>
              </div>
              
              <div className="flex flex-col md:flex-row gap-4">
                <button 
                  type="submit"
                  className="flex-1 bg-white text-primary-600 px-6 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors flex items-center justify-center touch-manipulation text-base"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Get Your Full Assessment Report
                </button>
                <button 
                  type="button"
                  onClick={() => window.open('https://trialconnx.com/contact', '_blank')}
                  className="flex-1 bg-success-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-success-700 transition-colors flex items-center justify-center touch-manipulation text-base"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book TrialConnx Demo
                </button>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center text-gray-500 text-sm">
              <span>Powered by</span>
              <img src={TrialConnxLogo} alt="TrialConnx" className="h-4 ml-2" />
            </div>
            <p className="text-gray-400 text-xs mt-2">
              Accelerating clinical research through smart feasibility solutions
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show process flow page
  return (
    <ProcessFlow 
      currentStep={currentStep}
      steps={steps}
      assessmentData={assessmentData}
      updateAssessmentData={updateAssessmentData}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
      isStepValid={isStepValid}
    />
  );
};

export default StudySpeedCalculator;